import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';

interface CredentialsCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

interface CredentialsStatusRow {
  client_key_masked: string | null;
  updated_at: string | null;
  status: string | null;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Zugangsdaten-Tab (/#/credentials, system.apps.{appId}.tabs). Lässt den Händler die
 * Kaufland-Seller-API-Keys nachträglich ändern, falls er sie im Seller-Portal neu
 * generiert. Lesen: maskierter Client Key + updated_at über kaufland_get_credentials_status
 * (Secret wird NIE zurückgegeben). Schreiben: über die bestehende kaufland-install-Function
 * (HMAC-Probe + Vault, idempotent) — falsche Daten überschreiben nie die funktionierenden.
 */
export async function renderCredentials(ctx: CredentialsCtx): Promise<void> {
  const host = ctx.contentEl;
  host.hidden = false;
  host.innerHTML = `
    <div class="kf-page" style="height:100%;display:flex;flex-direction:column;">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
          <span class="hdivider"></span>
          <div class="htitle"><div class="heyebrow">Kaufland · Zugangsdaten</div><h1>Kaufland Seller-API</h1></div>
          <div class="hactions"><span class="hstatus"><span class="d"></span>Kaufland verbunden</span></div>
        </header>
        <div class="scrollarea">
          <div class="card" style="max-width:560px;padding:20px 22px;">
            <div id="kl-cred-status" class="install-status install-status--info" style="margin-bottom:16px;">Lade Status …</div>

            <p class="hint" style="margin-top:0;">Client Key und Secret Key aus dem
              <a href="https://sellerportal.kaufland.de/settings/api" target="_blank" rel="noopener">Kaufland Seller Portal · Einstellungen → API</a>.
              Die Daten werden vor dem Speichern gegen Kaufland geprüft; bei einem Fehler bleiben die bisherigen Zugangsdaten unverändert.</p>

            <div class="kl-form-row" style="margin-bottom:12px;">
              <label for="kl-cred-client">Client Key</label>
              <div class="v-input"><input id="kl-cred-client" type="text" autocomplete="off" spellcheck="false" placeholder="neuer Client Key" /></div>
            </div>
            <div class="kl-form-row" style="margin-bottom:8px;">
              <label for="kl-cred-secret">Secret Key</label>
              <div class="v-input"><input id="kl-cred-secret" type="password" autocomplete="off" spellcheck="false" placeholder="neuer Secret Key" /></div>
            </div>

            <div class="kl-modal-error" id="kl-cred-error" hidden style="margin-bottom:8px;"></div>

            <div class="actions" style="margin-top:16px;display:flex;gap:8px;justify-content:flex-end;">
              <button type="button" class="v-button v-button--primary" id="kl-cred-save" disabled>
                <span class="v-icon fa-regular fa-shield-check" id="kl-cred-save-icon"></span>Speichern &amp; prüfen
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  `;

  const statusEl = host.querySelector<HTMLDivElement>('#kl-cred-status')!;
  const clientInp = host.querySelector<HTMLInputElement>('#kl-cred-client')!;
  const secretInp = host.querySelector<HTMLInputElement>('#kl-cred-secret')!;
  const errEl = host.querySelector<HTMLDivElement>('#kl-cred-error')!;
  const saveBtn = host.querySelector<HTMLButtonElement>('#kl-cred-save')!;
  const saveIcon = host.querySelector<HTMLElement>('#kl-cred-save-icon')!;

  async function loadStatus(): Promise<void> {
    const { data, error } = await ctx.supabase.rpc('kaufland_get_credentials_status');
    if (error) {
      statusEl.className = 'install-status install-status--error';
      statusEl.textContent = `Status konnte nicht geladen werden: ${error.message}`;
      return;
    }
    const row = (Array.isArray(data) ? data[0] : data) as CredentialsStatusRow | undefined;
    if (!row || !row.client_key_masked) {
      statusEl.className = 'install-status install-status--info';
      statusEl.textContent = 'Noch keine Zugangsdaten hinterlegt.';
      return;
    }
    statusEl.className = 'install-status install-status--ok';
    statusEl.innerHTML = `Zugangsdaten hinterlegt · Client Key <strong>${escapeHtml(row.client_key_masked)}</strong> · zuletzt geändert ${escapeHtml(formatDateTime(row.updated_at))}`;
  }

  const validate = () => {
    const ok = clientInp.value.trim().length > 0 && secretInp.value.trim().length > 0;
    saveBtn.disabled = !ok;
  };
  clientInp.addEventListener('input', validate);
  secretInp.addEventListener('input', validate);

  saveBtn.addEventListener('click', async () => {
    const clientKey = clientInp.value.trim();
    const secretKey = secretInp.value.trim();
    if (!clientKey || !secretKey) return;
    errEl.hidden = true;
    saveBtn.disabled = true;
    saveIcon.className = 'v-icon fa-regular fa-spinner-third fa-spin';
    try {
      const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/kaufland-install`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ clientKey, secretKey }),
      });
      const payload = (await res.json()) as { ok?: boolean; error?: { code: string; message: string } };
      if (!res.ok || !payload.ok) {
        const msg = payload.error?.message ?? `HTTP ${res.status}`;
        errEl.hidden = false;
        errEl.textContent = `Speichern fehlgeschlagen: ${msg}`;
        showToast('Zugangsdaten wurden nicht gespeichert.', 'error');
        return;
      }
      secretInp.value = '';
      clientInp.value = '';
      validate();
      showToast('Zugangsdaten aktualisiert und geprüft.', 'success');
      await loadStatus();
    } catch (e) {
      errEl.hidden = false;
      errEl.textContent = `Fehler: ${(e as Error).message}`;
      showToast('Zugangsdaten wurden nicht gespeichert.', 'error');
    } finally {
      saveIcon.className = 'v-icon fa-regular fa-shield-check';
      validate();
    }
  });

  validate();
  await loadStatus();
}
